/**
 * Interstitial Redirect
 *
 * Handles the redirect on interstitial pages.
 * Replicates legacy components/interstitial/initialize.js without jQuery
 * or Sprockets dependencies.
 *
 * Behavior:
 * - Waits 3 seconds, then redirects to window.IDme.endpoint
 * - Fires an "idmeredirect" custom event for analytics before redirecting
 * - Uses CJ navigate when the CJ SDK is present
 * - Skips the 3s delay for browser extension clicks when the tab is not focused
 * - With an upgraded [data-interstitial-continue] fragno-button on the page,
 *   schedules nothing and redirects on its click instead
 *   (click_interstitial_static_experiment; the server never renders the
 *   button for browser extension clicks)
 * - With a Continue button fragno never upgraded, falls back to the timed
 *   redirect and sends interstitial_static_fallback first
 */

import { dispatchTrack } from "../analytics/shop-analytics-track.js";

const REDIRECT_DELAY_MS = 3000;

function getRedirectFunction(endpoint) {
  if (typeof window.cj !== "undefined") {
    return () => window.cj.navigate(endpoint);
  }
  return () => window.location.assign(endpoint);
}

function isBrowserExtensionBackground() {
  return (
    window.location.search.includes("source=browser_extension") &&
    !document.hasFocus()
  );
}

// One redirect per click. A bfcache restore (Back from the merchant) brings the
// page back with this state intact, so pageshow re-arms the button.
function redirectOnClick(button, redirect) {
  let redirecting = false;

  button.addEventListener("click", () => {
    if (redirecting) return;
    redirecting = true;
    redirect();
  });

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) redirecting = false;
  });
}

export function initInterstitialRedirect() {
  const endpoint = window.IDme?.endpoint;
  if (!endpoint) return;

  const redirectElement = document.querySelector(
    '[data-trigger="idmeredirect"]',
  );
  const redirectFunction = getRedirectFunction(endpoint);

  // The observer listens for the data-trigger="idmeredirect" event; this
  // dispatches it to trigger tracking.
  const beforeRedirect = () => {
    if (redirectElement) {
      redirectElement.dispatchEvent(new Event("idmeredirect"));
    }

    redirectFunction();
  };

  let timedRedirect = beforeRedirect;

  const continueButton = document.querySelector("[data-interstitial-continue]");
  if (continueButton) {
    if (customElements.get("fragno-button")) {
      redirectOnClick(continueButton, beforeRedirect);
      return;
    }
    // Without fragno the button has no face to click; the timer still gets the
    // member to the merchant.
    console.warn("[Interstitial] fragno-button missing; using timed redirect");
    // This visitor's events say "static" but they got control's timed redirect, so
    // mark the session for exclusion from the readout. Sent at redirect time, not
    // now, so the analytics listener has had the same 3s to attach as control.
    timedRedirect = () => {
      dispatchTrack(continueButton, "interstitial_static_fallback");
      beforeRedirect();
    };
  }

  const timeout = isBrowserExtensionBackground() ? 0 : REDIRECT_DELAY_MS;
  setTimeout(timedRedirect, timeout);
}
